
import {defineComponent, nextTick, onMounted, PropType, reactive, ref, watch} from "vue";
import InputText from "primevue/inputtext";
import Password from 'primevue/password';
import {User} from "@dex/squeeze-client-ts";
import {email, required} from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {useSqueezeStore} from "@/apps/squeeze/store";
import {
	atLeastOneSpecialCharacterValidation,
	atLeastOneNumberValidation,
	minOneLowerAndUpperCaseValidation,
	passwordLengthValidation,
	whitespaceValidation
} from "@/util/ValidationHelper";

export default defineComponent({
	name: "UserForm",
	methods: {
		atLeastOneSpecialCharacterValidation,
		atLeastOneNumberValidation,
		minOneLowerAndUpperCaseValidation,
		passwordLengthValidation,
		whitespaceValidation,
	},
	components: {
		InputText,
		Password,
	},
	props: {
		userId: {
			type: Number,
			default: 0,
		},
		user: {
			type: Object as PropType<User>,
			required: true,
		},
		showErrorMessage: {
			type: Boolean,
		},
	},
	emits: ['update'],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();
		const store = useSqueezeStore();

		/** String with all password validation rules */
		const passwordValidationRules = t('Squeeze.Login.PasswordMustInclude') +
			' \n - ' + t('Forms.Val.PasswordLength') +
			' \n - ' + t('Forms.Val.MinOneLowerAndUpperCase', { field: t('Squeeze.Login.Password')}) +
			' \n - ' + t('Forms.Val.AtLeastOneNumber', { field: t('Squeeze.Login.Password')}) +
			' \n - ' + t('Forms.Val.AtLeastOneSpecialCharacter', { field: t('Squeeze.Login.Password')});

		/** Show loading? */
		const loading = ref(false);

		/** Current Object of all input-fields */
		const value = reactive<User>({
			login: '',
			firstName: '',
			lastName: '',
			email: '',
			password: '',
			externalUser: false,
		});

		/** Determines the required rules for validation */
		let rules = {
			login: { required, whitespaceValidation },
			firstName: { required },
			lastName: { required },
			email: { required, email},
			password: { },
		}

		if (!props.userId) {
			rules = {
				login: { required, whitespaceValidation },
				firstName: { required },
				lastName: { required },
				email: { required, email},
				password: {
					required,
					passwordLengthValidation,
					minOneLowerAndUpperCaseValidation,
					atLeastOneNumberValidation,
					atLeastOneSpecialCharacterValidation,
				},
			}
		}

		/** Use Vuelidate */
		const v$ = useVuelidate(rules, value);

		onMounted(async () => {
			Object.assign(value, props.user);

			// Emit Validated-Values onMounted. Otherwise there can be validation-errors when editing multiple entries
			await v$.value.$validate();
			emit("update", value, v$.value.$invalid);
		});

		/** Watch prop at set value object, because props are not allowed to be mutated */
		watch(props.user, async () => {
			Object.assign(value, props.user);
			await nextTick();
			emit("update", value, v$.value.$invalid);
		})

		/** Triggered on change of any field */
		const update = () => {
			emit("update", value, v$.value.$invalid);
		}

		return {
			t,
			toast,
			store,
			passwordValidationRules,
			loading,
			value,
			props,
			v$,
			update,
		}
	},
});

